// Interviews — bucket page for interview debriefs. The stax-interview-debrief
// skill generates each debrief in Claude Code (merging transcript + Gemini +
// hand notes) and pushes the report, one-pager, and structured record here.
// This tab is the read + analytics surface: every debrief in one place, plus
// rolling analytics recomputed from the records by the Worker.

import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  useInterviews,
  useInterview,
  useInterviewAnalytics,
  useGenerateUserStories,
  type InterviewListRow,
  type InterviewDetail,
  type InterviewRecord,
  type InterviewAggregates,
} from '../lib/queries';

type TabId = 'interviews' | 'analytics';
const VALID: TabId[] = ['interviews', 'analytics'];
const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'interviews', label: 'Interviews' },
  { id: 'analytics', label: 'Analytics' },
];

export function Interviews() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') as TabId | null;
  const tab: TabId = raw && VALID.includes(raw) ? raw : 'interviews';
  const id = params.get('id');

  const setTab = (next: TabId) => {
    params.set('tab', next);
    params.delete('id');
    setParams(params, { replace: true });
  };

  return (
    <div className="bucket">
      <nav className="bucket__tabs" aria-label="Interview views">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`bucket__tab ${tab === t.id ? 'bucket__tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <div className="bucket__body">
        {tab === 'interviews' && (id ? <InterviewDetailView id={id} /> : <InterviewsList />)}
        {tab === 'analytics' && <AnalyticsTab />}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// helpers

function humanize(s: string | null | undefined): string {
  if (!s) return '—';
  return s.replace(/_/g, ' ');
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function outcome(row: InterviewListRow): string {
  if (row.interview_type === 'partnership') return humanize(row.decision);
  return humanize(row.disposition);
}

// ----------------------------------------------------------------------------
// List

function InterviewsList() {
  const { data, isLoading } = useInterviews();
  const rows = data ?? [];

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section F</span>
          <span>Interviews</span>
          <span>{rows.length} debrief{rows.length === 1 ? '' : 's'}</span>
        </div>
        <h1 className="page__title">Interview Debriefs</h1>
        <p className="page__deck">
          Generated by the interview-debrief skill in Claude Code from the
          transcript, Gemini notes, and hand notes. Click a row for the full
          report and one-pager. The Analytics tab rolls every interview up.
        </p>
      </header>

      {isLoading && <p><em>Loading interviews…</em></p>}

      {!isLoading && rows.length === 0 && (
        <div className="podcast__empty">
          No debriefs yet. In Claude Code, paste an interview and run the
          stax-interview-debrief skill. It writes the report here automatically.
        </div>
      )}

      {rows.length > 0 && (
        <table className="interview__table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Who</th>
              <th>Outcome</th>
              <th>Confidence</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.interview_id} className="podcast__row">
                <td className="podcast__when">{fmtDate(r.date)}</td>
                <td>
                  <span className={`interview__type interview__type--${r.interview_type}`}>
                    {r.interview_type}
                  </span>
                </td>
                <td className="podcast__topic">
                  {r.title || r.counterpart || <span className="podcast__muted">—</span>}
                </td>
                <td>{outcome(r)}</td>
                <td>{humanize(r.coverage_confidence)}</td>
                <td>
                  <Link to={`/interviews?tab=interviews&id=${encodeURIComponent(r.interview_id)}`} className="podcast__detail-btn">
                    details →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Detail

function InterviewDetailView({ id }: { id: string }) {
  const { data, isLoading } = useInterview(id);
  const generate = useGenerateUserStories();
  const navigate = useNavigate();

  if (isLoading) return <div className="page"><p><em>Loading…</em></p></div>;
  if (!data) return <div className="page"><p>Interview not found.</p></div>;

  const rec = data.record ?? {};

  const downloadMd = (text: string, suffix: string) => {
    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.interview_id}-${suffix}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section F · Interviews</span>
          <span>{data.interview_type}</span>
          <span>{fmtDate(data.date)}</span>
        </div>
        <h1 className="page__title">{data.title || data.counterpart || data.interview_id}</h1>
        <div className="podcast__status-row">
          <span className="interview__meta">
            {data.interview_type === 'partnership'
              ? `Decision: ${humanize(data.decision)}`
              : `${humanize(data.familiarity)} · ${humanize(data.disposition)}`}
            {' · '}confidence {humanize(data.coverage_confidence)}
          </span>
          {data.interview_type === 'user' && (
            <button
              type="button"
              className="podcast__action-btn podcast__action-btn--primary interview__story-cta"
              disabled={generate.isPending}
              onClick={() =>
                generate.mutate(data.interview_id, { onSuccess: () => navigate('/user-stories') })
              }
            >
              {generate.isPending ? 'Generating…' : 'Generate user stories'}
            </button>
          )}
          <Link to="/interviews?tab=interviews" className="podcast__back-link">← back to interviews</Link>
        </div>
        {generate.isError && (
          <p className="painpoint__form-error">Could not generate stories. {generate.error?.message}</p>
        )}
      </header>

      <section className="podcast__detail-grid">
        <div className="podcast__detail-main">
          <h2 className="podcast__section-title">Report</h2>
          {data.report_md ? (
            <>
              <div className="podcast__actions">
                <button
                  type="button"
                  className="podcast__action-btn podcast__action-btn--primary"
                  onClick={() => navigator.clipboard.writeText(data.report_md ?? '')}
                >
                  Copy report
                </button>
                <button
                  type="button"
                  className="podcast__action-btn"
                  onClick={() => downloadMd(data.report_md ?? '', 'report')}
                >
                  Download .md
                </button>
              </div>
              <pre className="podcast__markdown">{data.report_md}</pre>
            </>
          ) : (
            <p className="podcast__muted">No report stored.</p>
          )}

          <h2 className="podcast__section-title">One-pager</h2>
          {data.one_pager_md ? (
            <>
              <div className="podcast__actions">
                <button
                  type="button"
                  className="podcast__action-btn podcast__action-btn--primary"
                  onClick={() => navigator.clipboard.writeText(data.one_pager_md ?? '')}
                >
                  Copy one-pager
                </button>
                <button
                  type="button"
                  className="podcast__action-btn"
                  onClick={() => downloadMd(data.one_pager_md ?? '', 'one-pager')}
                >
                  Download .md
                </button>
              </div>
              <pre className="podcast__markdown">{data.one_pager_md}</pre>
            </>
          ) : (
            <p className="podcast__muted">No one-pager stored.</p>
          )}
        </div>

        <aside className="podcast__detail-side">
          <KeyFacts data={data} rec={rec} />
        </aside>
      </section>
    </div>
  );
}

function KeyFacts({ data, rec }: { data: InterviewDetail; rec: InterviewRecord }) {
  const list = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x) => typeof x === 'string') as string[] : [];

  const actionItems = Array.isArray(rec.action_items) ? rec.action_items as Array<Record<string, unknown>> : [];
  const openQuestions = list(rec.open_questions);
  const waysForward = list(rec.ways_forward);
  const theirConcerns = Array.isArray(rec.their_concerns) ? rec.their_concerns as Array<Record<string, unknown>> : [];
  const notableQuotes = Array.isArray(rec.notable_quotes) ? rec.notable_quotes as Array<Record<string, unknown>> : [];
  const themes = (rec.themes ?? {}) as Record<string, unknown>;

  return (
    <>
      {data.interview_type === 'partnership' && waysForward.length > 0 && (
        <section className="podcast__side-block">
          <h3 className="podcast__side-title">Ways forward</h3>
          <ul className="interview__bullets">
            {waysForward.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </section>
      )}

      {data.interview_type === 'partnership' && theirConcerns.length > 0 && (
        <section className="podcast__side-block">
          <h3 className="podcast__side-title">What they pushed on</h3>
          <ul className="interview__bullets">
            {theirConcerns.map((c, i) => (
              <li key={i}>
                {String(c.concern ?? '')}
                {c.response ? <span className="podcast__muted"> — {String(c.response)}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.interview_type === 'user' && (
        <ThemeBlocks themes={themes} />
      )}

      {actionItems.length > 0 && (
        <section className="podcast__side-block">
          <h3 className="podcast__side-title">Action items</h3>
          <ul className="interview__bullets">
            {actionItems.map((a, i) => (
              <li key={i}>
                {String(a.task ?? '')}
                {a.owner ? <span className="podcast__muted"> — {String(a.owner)}</span> : null}
                {a.due ? <span className="podcast__muted"> ({String(a.due)})</span> : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      {openQuestions.length > 0 && (
        <section className="podcast__side-block">
          <h3 className="podcast__side-title">Open questions</h3>
          <ul className="interview__bullets">
            {openQuestions.map((q) => <li key={q}>{q}</li>)}
          </ul>
        </section>
      )}

      {notableQuotes.length > 0 && (
        <section className="podcast__side-block">
          <h3 className="podcast__side-title">Notable quotes</h3>
          <ul className="interview__bullets">
            {notableQuotes.map((q, i) => (
              <li key={i}>
                “{String(q.quote ?? '')}”
                {q.speaker ? <span className="podcast__muted"> — {String(q.speaker)}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function ThemeBlocks({ themes }: { themes: Record<string, unknown> }) {
  const blocks: Array<{ key: string; label: string }> = [
    { key: 'pain_points', label: 'Pain points' },
    { key: 'feature_requests', label: 'Feature requests' },
    { key: 'praise', label: 'Praise' },
    { key: 'confusion_bugs', label: 'Confusion / bugs' },
  ];
  const present = blocks.filter((b) => Array.isArray(themes[b.key]) && (themes[b.key] as unknown[]).length > 0);
  if (present.length === 0) return null;
  return (
    <>
      {present.map((b) => (
        <section key={b.key} className="podcast__side-block">
          <h3 className="podcast__side-title">{b.label}</h3>
          <ul className="interview__bullets">
            {(themes[b.key] as string[]).map((t) => <li key={t}>{t}</li>)}
          </ul>
        </section>
      ))}
    </>
  );
}

// ----------------------------------------------------------------------------
// Analytics

function AnalyticsTab() {
  const { data, isLoading } = useInterviewAnalytics();

  if (isLoading) return <div className="page"><p><em>Loading analytics…</em></p></div>;
  if (!data) return <div className="page"><p>No analytics yet.</p></div>;

  const agg: InterviewAggregates = data.aggregates;
  const totals = agg.totals;
  const questions = data.question_catalog.questions
    .slice()
    .sort((a, b) => b.times_asked - a.times_asked);
  const responses = data.response_catalog.responses
    .slice()
    .sort((a, b) => b.count - a.count);

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section F · Interviews</span>
          <span>Analytics</span>
          <span>{agg.updated ? `through ${fmtDate(agg.updated)}` : 'no data yet'}</span>
        </div>
        <h1 className="page__title">Interview Analytics</h1>
        <p className="page__deck">
          Rolled up from every debrief. Recomputed from the records on each load,
          so the numbers never drift.
        </p>
      </header>

      <div className="interview__stat-row">
        <StatCard label="Interviews" value={totals.interviews} />
        <StatCard label="Partnership" value={totals.partnership} />
        <StatCard label="User" value={totals.user} />
      </div>

      {totals.interviews === 0 && (
        <div className="podcast__empty">
          No interviews logged yet. Run the skill in Claude Code and the analytics fill in here.
        </div>
      )}

      {totals.partnership > 0 && (
        <section className="interview__analytics-section">
          <h2 className="podcast__section-title">Partnership</h2>
          <div className="interview__bar-grid">
            <BarBlock title="Decisions" counts={agg.partnership.decisions} />
            <BarBlock title="Compatibility structure" counts={agg.partnership.compatibility_structure} />
          </div>
        </section>
      )}

      {totals.user > 0 && (
        <section className="interview__analytics-section">
          <h2 className="podcast__section-title">User</h2>
          <div className="interview__bar-grid">
            <BarBlock title="Familiarity" counts={agg.user.familiarity} />
            <BarBlock title="Disposition" counts={agg.user.dispositions} />
            <BarBlock title="Sentiment" counts={agg.user.sentiment} />
            <BarBlock title="Response types" counts={agg.user.response_types} />
            <BarBlock title="Topics" counts={agg.user.topics} />
          </div>
        </section>
      )}

      {questions.length > 0 && (
        <section className="interview__analytics-section">
          <h2 className="podcast__section-title">Question catalog ({questions.length})</h2>
          <table className="interview__table">
            <thead>
              <tr><th>Question</th><th>Topic</th><th>Asked</th><th>Interviews</th><th>Last</th></tr>
            </thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.id} className="podcast__row">
                  <td className="podcast__topic">{q.text || q.id}</td>
                  <td>{humanize(q.topic)}</td>
                  <td>{q.times_asked}</td>
                  <td>{q.interviews}</td>
                  <td className="podcast__when">{fmtDate(q.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {responses.length > 0 && (
        <section className="interview__analytics-section">
          <h2 className="podcast__section-title">Response catalog ({responses.length})</h2>
          <table className="interview__table">
            <thead>
              <tr><th>Reaction</th><th>Type</th><th>Count</th><th>+ / · / −</th></tr>
            </thead>
            <tbody>
              {responses.map((r) => (
                <tr key={r.id} className="podcast__row">
                  <td className="podcast__topic">{r.text || r.id}</td>
                  <td>{humanize(r.type)}</td>
                  <td>{r.count}</td>
                  <td className="interview__sentiment">
                    {r.sentiment_breakdown.positive} / {r.sentiment_breakdown.neutral} / {r.sentiment_breakdown.negative}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="interview__stat-card">
      <span className="interview__stat-value">{value}</span>
      <span className="interview__stat-label">{label}</span>
    </div>
  );
}

function BarBlock({ title, counts }: { title: string; counts: Record<string, number> }) {
  const entries = Object.entries(counts);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="interview__bar-block">
      <h3 className="podcast__side-title">{title}</h3>
      {entries.map(([k, v]) => (
        <div key={k} className="interview__bar-row">
          <span className="interview__bar-label">{humanize(k)}</span>
          <span className="interview__bar-track">
            <span className="interview__bar-fill" style={{ width: `${(v / max) * 100}%` }} />
          </span>
          <span className="interview__bar-count">{v}</span>
        </div>
      ))}
    </div>
  );
}
